import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Funnel } from 'lucide-react';
import { Text, UnstyledButton } from '@/shared/ui';
import { getCategoryAccentColor } from '../category-accent';
import { getCategoryLabel } from '../PropertyFormDualPane/schema';
import type { GroupAggregate, GroupBy, PropertyGroup } from '../../pages/property-grouping';
import { GroupSplitBar } from './group-split-bar';
import { formatArea, formatMoney, PENDING_METRIC } from './group-metrics';

interface GroupHeaderRowProps {
  /** The active grouping dimension (controls label colour + split visibility). */
  by: Exclude<GroupBy, 'none'>;
  /** The group this header summarizes. */
  group: PropertyGroup;
  /** The group's aggregate stats, or null while they are still pending. */
  aggregate: GroupAggregate | null;
  /** Whether the group is currently collapsed. */
  collapsed: boolean;
  /** Toggles the group open/closed. */
  onToggle: () => void;
  /** Narrows the whole list to this group (does not toggle collapse). */
  onFilterGroup: () => void;
}

/** Uppercase caption under a metric value (MÉDIA, MEDIANA VENDA, …). */
const captionStyle: React.CSSProperties = {
  fontSize: 'var(--text-tag)',
  letterSpacing: 'var(--tracking-chip, 0.06em)',
  textTransform: 'uppercase',
  fontWeight: 'var(--font-semibold)' as unknown as number,
  color: 'var(--text-tertiary)',
  lineHeight: 1.2,
};

const metricValueStyle: React.CSSProperties = {
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-semibold)' as unknown as number,
  color: 'var(--text-primary)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-2-5) var(--space-4)',
  verticalAlign: 'middle',
};

/**
 * The aggregate header row for one group, laid into the same five compact
 * columns as the listing rows below it. The whole row is a toggle (keyboard
 * reachable via the real chevron button), and the trailing funnel narrows the
 * list to this group without collapsing it.
 *
 * @param props - The group, its aggregate, and the row's handlers.
 * @returns The group header `<tr>`.
 */
export const GroupHeaderRow: React.FC<GroupHeaderRowProps> = ({
  by,
  group,
  aggregate,
  collapsed,
  onToggle,
  onFilterGroup,
}) => {
  const { t } = useTranslation('features/properties');
  const count = aggregate?.count ?? group.items.length;
  const field = t(`list.grouping.options.${by}`);

  const topCategory = aggregate?.topCategory ?? null;
  const avgArea = aggregate ? formatArea(aggregate.avgTotalArea) : PENDING_METRIC;

  // Median sale leads; a group with no sales falls back to the median rent
  // with its own caption, so the figure is never silently mislabelled.
  const hasSale = aggregate?.medianSalePrice != null;
  const priceValue = aggregate
    ? hasSale
      ? formatMoney(aggregate.medianSalePrice)
      : formatMoney(aggregate.medianRentalPrice)
    : PENDING_METRIC;
  const priceCaption = hasSale
    ? t('list.grouping.captionMedianSale')
    : aggregate && aggregate.medianRentalPrice != null
      ? t('list.grouping.captionMedianRental')
      : t('list.grouping.captionMedianSale');
  const priceSuffix = aggregate && !hasSale && aggregate.medianRentalPrice != null ? t('list.perMonth') : '';

  return (
    <>
      <td style={{ ...cellStyle, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
          <UnstyledButton
            type="button"
            data-testid="group-chevron"
            aria-label={collapsed ? t('list.grouping.expandGroup', { group: group.label }) : t('list.grouping.collapseGroup', { group: group.label })}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              flex: 'none',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            <span
              className="motion-safe:transition-transform"
              style={{ display: 'inline-flex', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'var(--transition-standard)' }}
            >
              <ChevronDownIcon width={16} height={16} />
            </span>
          </UnstyledButton>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Text
                as="span"
                style={{
                  fontSize: 'var(--text-app-subheading)',
                  fontWeight: 'var(--font-semibold)' as unknown as number,
                  color: 'var(--text-primary)',
                }}
              >
                {group.label}
              </Text>
              {group.subLabel && (
                <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
                  {group.subLabel}
                </Text>
              )}
              <Text
                as="span"
                data-testid="group-count-pill"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 var(--space-2)',
                  height: 20,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--surface-tertiary)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-tag)',
                  fontWeight: 'var(--font-semibold)' as unknown as number,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t('list.grouping.propertyCount', { count })}
              </Text>
            </div>
            {/* The split would merely echo the group label when grouping by
                business type, so it is hidden in that one case. */}
            {by !== 'business' && aggregate && <GroupSplitBar split={aggregate.byBusinessType} variant="inline" />}
          </div>
        </div>
      </td>

      <td style={{ ...cellStyle, width: '144px' }}>
        {topCategory ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-body-sm)',
                fontWeight: 'var(--font-semibold)' as unknown as number,
                color: getCategoryAccentColor(topCategory.value),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {getCategoryLabel(topCategory.value, t) || topCategory.value}
            </Text>
            <Text as="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
              {topCategory.distinct > 1
                ? t('list.grouping.topCategoryNoteMore', {
                    count: topCategory.count,
                    total: count,
                    more: topCategory.distinct - 1,
                  })
                : t('list.grouping.topCategoryNote', { count: topCategory.count, total: count })}
            </Text>
          </div>
        ) : (
          <Text as="span" style={{ color: 'var(--text-tertiary)' }}>{PENDING_METRIC}</Text>
        )}
      </td>

      <td style={{ ...cellStyle, width: '112px', textAlign: 'right' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span style={metricValueStyle}>{avgArea}</span>
          <span style={captionStyle}>{t('list.grouping.captionAverage')}</span>
        </div>
      </td>

      <td style={{ ...cellStyle, width: '168px', textAlign: 'right' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span style={metricValueStyle}>
            {priceValue}
            {priceSuffix && (
              <span style={{ fontSize: 'var(--text-meta)', fontWeight: 500, color: 'var(--text-secondary)' }}>{priceSuffix}</span>
            )}
          </span>
          <span style={captionStyle}>{priceCaption}</span>
        </div>
      </td>

      <td style={{ ...cellStyle, width: '48px', textAlign: 'right' }}>
        <UnstyledButton
          type="button"
          data-testid="group-filter-action"
          aria-label={t('list.grouping.filterGroup', { field, group: group.label })}
          title={t('list.grouping.filterGroup', { field, group: group.label })}
          onClick={(e) => { e.stopPropagation(); onFilterGroup(); }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Funnel size={16} />
        </UnstyledButton>
      </td>
    </>
  );
};
